/**
 * Bottom Sheet Component
 *
 * A mobile-friendly bottom sheet with named sheets and global state management.
 * Only one sheet can be open at a time. Features drag-to-dismiss and escape key support.
 *
 * @example
 * // 1. Add Provider at app root
 * <BottomSheet.Provider>
 *   <App />
 * </BottomSheet.Provider>
 *
 * // 2. Define sheets anywhere in your app
 * <BottomSheet.Sheet name="settings" ariaLabel="Settings">
 *   <BottomSheet.Header>
 *     <BottomSheet.Title>Settings</BottomSheet.Title>
 *   </BottomSheet.Header>
 *   <Content />
 * </BottomSheet.Sheet>
 *
 * // 3. Trigger them from anywhere
 * <BottomSheet.Trigger sheet="settings" asChild>
 *   <Button>Open Settings</Button>
 * </BottomSheet.Trigger>
 */
import { FocusScope } from '@radix-ui/react-focus-scope';
import { AnimatePresence, motion } from 'motion/react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

const BottomSheetContext = React.createContext<{
  openSheet: string | null;
  setOpenSheet: (sheet: string | null) => void;
} | null>(null);

function useBottomSheet() {
  const context = React.useContext(BottomSheetContext);
  if (!context) {
    throw new Error(
      'BottomSheet components must be used within BottomSheet.Provider'
    );
  }
  return context;
}

/**
 * Global provider for bottom sheet state. Place at app root.
 * Manages which sheet is currently open (only one at a time).
 */
function Provider({ children }: { children: React.ReactNode }) {
  const [openSheet, setOpenSheet] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!openSheet) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpenSheet(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [openSheet]);

  return (
    <BottomSheetContext.Provider value={{ openSheet, setOpenSheet }}>
      {children}
    </BottomSheetContext.Provider>
  );
}

/**
 * Opens a bottom sheet by name when clicked.
 * @param sheet - Name of the sheet to open (must match a Sheet's name prop)
 * @param asChild - If true, clones the child element and adds onClick handler
 * @example
 * <BottomSheet.Trigger sheet="my-sheet" asChild>
 *   <Button>Open</Button>
 * </BottomSheet.Trigger>
 */
function BottomSheetTrigger({
  children,
  asChild,
  sheet,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean; sheet: string }) {
  const { setOpenSheet } = useBottomSheet();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpenSheet(sheet);
    props.onClick?.(e);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
      ...props,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button data-slot="drawer-trigger" onClick={handleClick} {...props}>
      {children}
    </button>
  );
}

/**
 * Closes the currently open bottom sheet.
 * @param asChild - If true, clones the child element and adds onClick handler
 */
function BottomSheetClose({
  children,
  asChild,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
  const { setOpenSheet } = useBottomSheet();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpenSheet(null);
    props.onClick?.(e);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
      ...props,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button data-slot="drawer-close" onClick={handleClick} {...props}>
      {children}
    </button>
  );
}

function BottomSheetPortal({
  children,
  container,
}: {
  children: React.ReactNode;
  container?: Element | null;
}) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return createPortal(children, container || document.body);
}

interface BottomSheetOverlayProps {
  className?: string;
}

function BottomSheetOverlay({ className }: BottomSheetOverlayProps) {
  const { setOpenSheet } = useBottomSheet();

  return (
    <motion.div
      key="backdrop"
      data-slot="drawer-overlay"
      className={cn('fixed inset-0 z-40 bg-black/50', className)}
      role="presentation"
      aria-hidden="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={() => setOpenSheet(null)}
    />
  );
}

function BottomSheetDragHandle({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-drag-handle"
      className={cn(
        'mx-auto mt-4 h-2 w-[60px] shrink-0 rounded-full',
        'bg-surface',
        className
      )}
      {...props}
    />
  );
}

/**
 * Defines a bottom sheet that can be opened by triggers.
 * Only renders when openSheet matches this sheet's name.
 * @param name - Unique identifier for this sheet (used by Trigger's sheet prop)
 * @param ariaLabel - Accessible label for the dialog
 * @param container - Optional portal container (defaults to document.body)
 * @example
 * <BottomSheet.Sheet name="settings" ariaLabel="Settings">
 *   <BottomSheet.Header>
 *     <BottomSheet.Title>Settings</BottomSheet.Title>
 *   </BottomSheet.Header>
 *   <Content />
 * </BottomSheet.Sheet>
 */
function BottomSheetSheet({
  className,
  children,
  container,
  ariaLabel = 'Bottom sheet',
  name,
}: {
  className?: string;
  children: React.ReactNode;
  container?: Element | null;
  ariaLabel?: string;
  name: string;
}) {
  const { openSheet, setOpenSheet } = useBottomSheet();
  const isOpen = openSheet === name;

  return (
    <BottomSheetPortal container={container}>
      <AnimatePresence>
        {isOpen && (
          <FocusScope trapped loop>
            <BottomSheetOverlay />

            <motion.div
              key="drawer"
              data-slot="drawer-content"
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              tabIndex={-1}
              className={cn(
                'fixed inset-x-0 bottom-0 z-50',
                'flex h-auto max-h-[80vh] flex-col',
                'bg-background',
                'rounded-t-2xl border-t border-border',
                'shadow-xl',
                className
              )}
              initial={{ y: '100%' }}
              animate={{
                y: 0,
                transition: {
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1],
                },
              }}
              exit={{
                y: '100%',
                transition: {
                  duration: 0.2,
                  ease: [0.4, 0, 1, 1],
                },
              }}
              drag="y"
              dragDirectionLock
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.3 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 100 || info.velocity.y > 500) {
                  setOpenSheet(null);
                }
              }}
            >
              <BottomSheetDragHandle />
              {children}
            </motion.div>
          </FocusScope>
        )}
      </AnimatePresence>
    </BottomSheetPortal>
  );
}

function BottomSheetHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        'flex flex-col gap-0.5 p-4 text-center md:gap-1.5 md:text-left',
        className
      )}
      {...props}
    />
  );
}

function BottomSheetFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  );
}

function BottomSheetTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2
      data-slot="drawer-title"
      className={cn('text-foreground font-semibold', className)}
      {...props}
    />
  );
}

function BottomSheetDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export const BottomSheet = {
  Provider: Provider,
  Portal: BottomSheetPortal,
  Overlay: BottomSheetOverlay,
  Trigger: BottomSheetTrigger,
  Close: BottomSheetClose,
  Sheet: BottomSheetSheet,
  DragHandle: BottomSheetDragHandle,
  Header: BottomSheetHeader,
  Footer: BottomSheetFooter,
  Title: BottomSheetTitle,
  Description: BottomSheetDescription,
  useBottomSheet: useBottomSheet,
};
